import { useCallback, useMemo, useState } from 'react'

import {
  ContactWorkflowStatus,
  IContact,
  TContactAdditionalForm,
  TContactCommonForm,
  TContactGeneralForm,
  TContactHPRolesForm,
} from 'clickNgo/entities/contact'
import { createContact, mapContactModifiedValues } from 'clickNgo/entities/contact/api'
import { replacementContact } from 'clickNgo/entities/contact/api/replacementContact'
import { PartnerHierarchyType } from 'clickNgo/entities/partner/configs'
import { IPartner } from 'clickNgo/entities/partner/models'
import { getJSendErrorMessages } from 'clickNgo/helpers/api'
import { useAppSource } from 'clickNgo/hooks/utils/useAppSource'
import { I375Form } from 'clickNgo/models/crud/forms'
import { useGetFormData, useGetFormDataField } from 'cng-common/features/form'
import { guid } from 'core/classes/helpers/GuidHelper'
import { TWizardStep } from 'shared/components/wizard'

import { ContactCreatorSteps, CreateContactFormContent } from '../../../../configs'
import { IContactFormKeys } from '../../../../models'
import { contactCreatorTranslation } from '../../../../translation'
import { HPRolesStep } from '../HPRolesStep'

interface IProps {
  contact: IContact
  partner: IPartner
  formKeys: IContactFormKeys
  onCreate: (contact: IContact) => Promise<void> | void
  onClose: () => Promise<void> | void
  isReplacementContact?: boolean
  source?: string
}

export function useHPRolesStep({
  contact,
  partner,
  formKeys,
  onCreate,
  isReplacementContact = false,
  source: sourceProp,
}: IProps) {
  const source = useAppSource(contact.source)
  const resolvedSource = sourceProp || source
  const [isNextStepDisabled] = useGetFormDataField<boolean>(
    formKeys.settings,
    CreateContactFormContent.fields.isNextStepDisabled.field
  )

  const [errorMessages, setErrorMessages] = useState<string[]>([])
  const [generalFormData] = useGetFormData<TContactGeneralForm>(formKeys.general)
  const [commonFormData] = useGetFormData<TContactCommonForm>(formKeys.common)
  const [hpRolesFormData] = useGetFormData<TContactHPRolesForm>(formKeys.hpRoles)
  const [additionalFormData] = useGetFormData<TContactAdditionalForm>(formKeys.additional)
  const modifiedValues = useMemo(
    () => [
      ...generalFormData.modifiedFields,
      ...commonFormData.modifiedFields,
      ...hpRolesFormData.modifiedFields,
      ...additionalFormData.modifiedFields,
    ],
    [
      additionalFormData.modifiedFields,
      commonFormData.modifiedFields,
      generalFormData.modifiedFields,
      hpRolesFormData.modifiedFields,
    ]
  )

  const isOnboardingRequest = [
    PartnerHierarchyType.AFFILIATE,
    PartnerHierarchyType.RESELLER_NETWORK,
  ].includes(partner.hierarchyType)

  const content = useCallback(
    () => (
      <HPRolesStep
        contact={contact}
        partner={partner}
        formKeys={formKeys}
        errorMessages={errorMessages}
      />
    ),
    [contact, errorMessages, formKeys, partner]
  )

  const saveContact = useCallback(
    async (workflowStatus: ContactWorkflowStatus) => {
      setErrorMessages([])
      const replacementDraftContactId =
        isReplacementContact && workflowStatus === ContactWorkflowStatus.Draft
          ? `new-${guid()}`
          : contact.contactId

      const data = {
        ...contact,
        ...generalFormData.formData,
        ...commonFormData.formData,
        ...hpRolesFormData.formData,
        ...additionalFormData.formData,
        contactId: replacementDraftContactId,
        entryId379: contact.entryId379,
        source: resolvedSource,
        workflowStatus,
      }

      if (isReplacementContact) {
        const updateContactResponse = await replacementContact(
          {
            ...data,
            modifiedValues,
          },
          { forceRoles: true }
        )

        if (updateContactResponse.status === 'fail') {
          setErrorMessages(getJSendErrorMessages(updateContactResponse))
          return
        }

        if (updateContactResponse.status === 'error') {
          const errorMessages = updateContactResponse.message ? [updateContactResponse.message] : []
          setErrorMessages(errorMessages)
          return
        }

        if (updateContactResponse.status === 'success') {
          const updatedContact: IContact = {
            ...data,
            ...updateContactResponse.data,
            modifiedValues: updateContactResponse.data.modifiedValues
              ? mapContactModifiedValues(
                  updateContactResponse.data.modifiedValues.split(',') as (keyof I375Form)[]
                )
              : modifiedValues,
            modified: new Date().toISOString(),
          }

          return onCreate(updatedContact)
        }
      }

      const createContactResponse = await createContact(data)

      if (createContactResponse && createContactResponse.status === 'fail') {
        setErrorMessages(getJSendErrorMessages(createContactResponse))
        return
      }

      if (createContactResponse && createContactResponse.status === 'error') {
        const errorMessages = createContactResponse.message ? [createContactResponse.message] : []
        setErrorMessages(errorMessages)
        return
      }

      if (createContactResponse && createContactResponse.status === 'success') {
        let newContact: IContact = { ...data, modified: new Date().toISOString() }

        if (
          createContactResponse.data.workflowStatus === ContactWorkflowStatus.Done &&
          createContactResponse.data.contactId.startsWith('new-')
        ) {
          newContact = {
            ...newContact,
            contactId: createContactResponse.data.contactId,
            entryId379: createContactResponse.data.entryId379,
          }
        } else {
          newContact = {
            ...newContact,
            ...createContactResponse.data,
          }
        }

        return onCreate(newContact)
      }

      return {
        hasError: false,
        message: 'Something went wrong',
      }
    },
    [
      additionalFormData.formData,
      commonFormData.formData,
      contact,
      generalFormData.formData,
      hpRolesFormData.formData,
      onCreate,
      resolvedSource,
      isReplacementContact,
      modifiedValues,
    ]
  )

  const resolveWorkflowStatus = useCallback(() => {
    const isDummyPartner =
      partner.partnerId.startsWith('new-') || partner.partnerId.startsWith('portal-')

    if (isDummyPartner && isOnboardingRequest) {
      return ContactWorkflowStatus.Draft
    }

    return ContactWorkflowStatus.New
  }, [partner.partnerId, isOnboardingRequest])

  const onStepChange = useCallback(
    async (prevStep: number, nextStep: number) => {
      if (prevStep > nextStep) {
        return
      }

      return saveContact(resolveWorkflowStatus())
    },
    [saveContact, resolveWorkflowStatus]
  )

  const onSaveAsDraft = useCallback(async () => {
    await saveContact(ContactWorkflowStatus.Draft)
  }, [saveContact])

  const hpRolesStep = useMemo<TWizardStep>(
    () => ({
      id: ContactCreatorSteps.HPRoles,
      title: ContactCreatorSteps.HPRoles,
      content,
      completeButtonContent: contactCreatorTranslation.steps.review.buttons.complete,
      showSaveAsDraft: isReplacementContact || resolvedSource.startsWith('Onboarding'),
      saveAsDraftButtonContent: 'Save as Draft',
      onSaveAsDraft,
      onStepChange,
      isNextStepDisabled,
    }),
    [content, isNextStepDisabled, isReplacementContact, onStepChange, onSaveAsDraft, resolvedSource]
  )

  return { hpRolesStep }
}
